import { Injectable } from '@angular/core';
import {
  VcrData,
  VcrTotals,
  QuoteDetail,
  QuoteLineCharges,
  QuoteAdditionalCharge,
  QuoteSalesAdditionalCharge,
  QuoteSalesDetail,
  PartSalesAdditionalCharge,
  PartSalesDetail,
  SubSystemTransactionType,
  WorkOrderAdditionalCharge,
  WorkOrderCase,
  WorkOrderLabour,
  MessageType,
} from '@quorum/models/xs-resource';

// Warranty submission types the VCR books into customer-facing totals.
const BOOKABLE_WARRANTY_TYPES = ['CP', 'P2', 'P3', 'P4'];

/**
 * A figure the DMS has not calculated yet arrives null; an explicit 0 is a real, calculated
 * value (an exempt line stores 0 tax, not null).
 */
const isPresent = (value: number): boolean => value !== null && value !== undefined;

@Injectable()
export class VcrService {
  /**
   * The PI flag (goodwrenchFlag) on a labour's service-group rate. The advisor preview's
   * client-assembled payload embeds it at serviceGroup.serviceGroupRate; the customer copy's
   * public/vcr-data payload embeds it only at serviceGroup.embedded.rate. Prefer
   * serviceGroupRate and fall back — each payload shape keeps its legacy gate.
   */
  private isLabourPriceIncluded(labour: WorkOrderLabour): boolean {
    const rate = labour?.embedded?.serviceGroup?.serviceGroupRate ?? labour?.embedded?.serviceGroup?.embedded?.rate;
    return rate?.goodwrenchFlag === '1';
  }

  /**
   * Tax for one booked line: the DMS-stamped figures when present, a per-line derived
   * estimate when they have not been stamped yet.
   *
   * A line quoted from PowerLane arrives with null gst/pst until the DMS calculates and
   * stamps them — null means "not calculated yet", not "exempt" (the DMS stores an explicit
   * 0 on exempt lines). Summing the nulls used to coerce them to zero tax (XS-11686).
   * Each tax type falls back independently: a stored figure (a genuine 0 included) always
   * wins; a null derives base × rate against the dealer's settings — per-line derivation,
   * per the XS-11687 investigation. Callers gate the base on the line's taxable flag where
   * the payload carries one. When the dealer settings offer no rate either, the derived
   * contribution is 0 — with no figure available from any source there is deliberately
   * nothing to estimate.
   */
  private getLineTax(
    vcrData: VcrData,
    storedGst: number | null,
    storedPst: number | null,
    gstBase: number,
    pstBase: number
  ): number {
    return (storedGst ?? this.deriveGst(vcrData, gstBase)) + (storedPst ?? this.derivePst(vcrData, pstBase));
  }

  private deriveGst(vcrData: VcrData, base: number): number {
    return vcrData.associateClass?.gstFlag === '1' && vcrData.gstSettings
      ? base * (vcrData.gstSettings.gstPercent / 100)
      : 0;
  }

  private derivePst(vcrData: VcrData, base: number): number {
    return vcrData.associateClass?.pstFlag === '1' && vcrData.pstSettings
      ? base * (vcrData.pstSettings.pstPercent / 100)
      : 0;
  }

  getAdditionalChargesForQuoteLine(vcrData: VcrData, quoteLine: QuoteDetail): QuoteLineCharges {
    const additionalCharges: QuoteLineCharges = {
      subTotal: 0,
      gstAmount: 0,
      pstAmount: 0,
    };

    vcrData.quoteAdditionalCharges.forEach((qac: QuoteAdditionalCharge) => {
      if (
        qac.workOrderChargeTypeId !== 'SHO' &&
        qac.workOrderChargeTypeId !== 'LAD' &&
        qac.workOrderChargeTypeId !== 'PAD' &&
        qac.quoteDetailSequence === quoteLine.sequence
      ) {
        /**
         * Check the PI flag (goodwrenchFlag) on the embedded service group rate.
         * If PI flag is '0' (No), the quote additional charges will be charged out
         * above the rate price. If PI flag on the service group rate is '1'
         * (Yes; meaning that all the pricing is included in the rate), we have to further check the
         * PI flag of the specific quote additional charge. If the quote additional charge PI flag is '0' (No),
         * charge out the additional charge above the rate price
         */
        if (quoteLine?.embedded?.serviceGroup?.embedded?.rate?.goodwrenchFlag === '1') {
          if (qac.goodwrenchFlag !== '1') {
            additionalCharges.subTotal += qac.subTotal;
            additionalCharges.gstAmount += qac.gstAmount;
            additionalCharges.pstAmount += qac.pstAmount;
          }
        } else {
          additionalCharges.subTotal += qac.subTotal;
          additionalCharges.gstAmount += qac.gstAmount;
          additionalCharges.pstAmount += qac.pstAmount;
        }
      }
    });

    return additionalCharges;
  }

  getPartSalesForQuoteLine(vcrData: VcrData, quoteLine: QuoteDetail): QuoteLineCharges {
    const additionalCharges: QuoteLineCharges = {
      subTotal: 0,
      gstAmount: 0,
      pstAmount: 0,
    };

    vcrData.quoteSalesDetails.forEach((qsd: QuoteSalesDetail) => {
      /**
       * Check the PI flag (goodwrenchFlag) on the embedded service group rate.
       * If PI flag is '0' (No), the parts will be charged out
       * above the rate price. If PI flag on the service group rate is '1'
       * (Yes; meaning that all the pricing is included in the rate), we have to further check the
       * PI flag of the specific part line. If the part line PI flag is '0' (No),
       * charge out the parts above the rate price
       */
      if (quoteLine?.embedded?.serviceGroup?.embedded?.rate?.goodwrenchFlag === '1') {
        if (qsd.quoteDetailSequence === quoteLine.sequence && qsd.goodwrenchFlag !== '1') {
          additionalCharges.subTotal += qsd.amount;
          additionalCharges.gstAmount += qsd.gstAmount;
          additionalCharges.pstAmount += qsd.pstAmount;
        }
      } else {
        if (qsd.quoteDetailSequence === quoteLine.sequence) {
          additionalCharges.subTotal += qsd.amount;
          additionalCharges.gstAmount += qsd.gstAmount;
          additionalCharges.pstAmount += qsd.pstAmount;
        }
      }
    });

    return additionalCharges;
  }

  getPartSalesAdditionalChargesForQuoteLine(vcrData: VcrData, quoteLine: QuoteDetail): QuoteLineCharges {
    const additionalCharges: QuoteLineCharges = {
      subTotal: 0,
      gstAmount: 0,
      pstAmount: 0,
    };

    vcrData.quoteSalesAdditionalCharges.forEach((qsac: QuoteSalesAdditionalCharge) => {
      /**
       * Check the PI flag (goodwrenchFlag) on the embedded service group rate.
       * If PI flag is '0' (No), the parts additional charges will be charged out
       * above the rate price. If PI flag on the service group rate is '1'
       * (Yes; meaning that all the pricing is included in the rate), we have to further check the
       * PI flag of the specific parts additional charges line. If the parts additional charges line PI flag is '0' (No),
       * charge out the parts additional charges above the rate price
       */
      if (quoteLine?.embedded?.serviceGroup?.embedded?.rate?.goodwrenchFlag === '1') {
        if (qsac.quoteDetailSequence === quoteLine.sequence && qsac.goodwrenchFlag !== '1') {
          additionalCharges.subTotal += qsac.subTotal;
          additionalCharges.gstAmount += qsac.gstAmount;
          additionalCharges.pstAmount += qsac.pstAmount;
        }
      } else {
        if (qsac.quoteDetailSequence === quoteLine.sequence) {
          additionalCharges.subTotal += qsac.subTotal;
          additionalCharges.gstAmount += qsac.gstAmount;
          additionalCharges.pstAmount += qsac.pstAmount;
        }
      }
    });

    return additionalCharges;
  }

  getAllAdditionalChargesForQuoteLine(vcrData: VcrData, quoteLine: QuoteDetail): QuoteLineCharges {
    const additionalCharges = this.getAdditionalChargesForQuoteLine(vcrData, quoteLine);
    const partSales = this.getPartSalesForQuoteLine(vcrData, quoteLine);
    const partSalesAdditionalCharges = this.getPartSalesAdditionalChargesForQuoteLine(vcrData, quoteLine);
    return {
      subTotal: additionalCharges.subTotal + partSales.subTotal + partSalesAdditionalCharges.subTotal,
      gstAmount: additionalCharges.gstAmount + partSales.gstAmount + partSalesAdditionalCharges.gstAmount,
      pstAmount: additionalCharges.pstAmount + partSales.pstAmount + partSalesAdditionalCharges.pstAmount,
    };
  }

  getAdditionalChargesForWorkOrderLabourLine(vcrData: VcrData, labour: WorkOrderLabour) {
    const additionalCharges = {
      subTotal: 0,
      tax: 0,
    };

    vcrData.workOrderAdditionalCharges.forEach((woac: WorkOrderAdditionalCharge) => {
      // Shop-supply / labour-adjustment / parts-adjustment charges are handled by the
      // shop-supplies calculation, not booked as additional charges. The advisor preview
      // pre-filters them at fetch time; the public/vcr-data payload arrives unfiltered.
      if (woac.chargeTypeId === 'SHO' || woac.chargeTypeId === 'LAD' || woac.chargeTypeId === 'PAD') {
        return;
      }
      /**
       * Check the PI flag (goodwrenchFlag) on the embedded service group rate.
       * If PI flag is '0' (No), the WO additional charges will be charged out
       * above the rate price. If PI flag on the service group rate is '1'
       * (Yes; meaning that all the pricing is included in the rate), we have to further check the
       * PI flag of the specific WO additional charge. If the WO additional charge PI flag is '0' (No),
       * charge out the WO additional charge above the rate price
       */
      if (this.isLabourPriceIncluded(labour)) {
        if (woac.warrantyCaseNumber === labour.warrantyCase && woac.goodwrenchFlag !== '1') {
          additionalCharges.subTotal += woac.subTotal;
          additionalCharges.tax += woac.pstAmount + woac.gstAmount;
        }
      } else {
        if (woac.warrantyCaseNumber === labour.warrantyCase) {
          additionalCharges.subTotal += woac.subTotal;
          additionalCharges.tax += woac.pstAmount + woac.gstAmount;
        }
      }
    });

    return additionalCharges;
  }

  getPartSalesForWorkOrderLabourLine(vcrData: VcrData, labour: WorkOrderLabour) {
    const partSales = {
      subTotal: 0,
      tax: 0,
    };

    vcrData.partSalesDetails.forEach((psd: PartSalesDetail) => {
      /**
       * Check the PI flag (goodwrenchFlag) on the embedded service group rate.
       * If PI flag is '0' (No), the WO parts will be charged out
       * above the rate price. If PI flag on the service group rate is '1'
       * (Yes; meaning that all the pricing is included in the rate), we have to further check the
       * PI flag of the specific part line. If the part line PI flag is '0' (No),
       * charge out the WO parts above the rate price
       */
      if (this.isLabourPriceIncluded(labour)) {
        if (psd.warrantyCase === labour.warrantyCase && psd.goodwrenchFlag !== '1') {
          partSales.subTotal += psd.amount;
          partSales.tax += psd.pstAmount + psd.gstAmount;
        }
      } else {
        if (psd.warrantyCase === labour.warrantyCase) {
          partSales.subTotal += psd.amount;
          partSales.tax += psd.pstAmount + psd.gstAmount;
        }
      }
    });

    return partSales;
  }

  /**
   * Part additional charges for a booked labour line: the DMS-stamped part-sales rows when
   * the labour's case has any, otherwise a fallback derived from the quote's part-sales
   * charge rows.
   *
   * A work order created from a PowerLane appointment books its part sales without their
   * additional charges (e.g. a tire levy) — the DMS only stamps part_sales_additional_charge
   * rows once the WO Part Sales window is opened (XS-11711). The quote line the labour was
   * created from (labour.quoteCaseNumber) still carries the charges PowerLane quoted, so an
   * unstamped case derives from those instead of dropping the charge. A case with any
   * stamped rows books only the stamped rows — the DMS may legitimately have stamped a
   * different set than was quoted.
   */
  getPartSalesAdditionalChargesForWorkOrderLabourLine(vcrData: VcrData, labour: WorkOrderLabour) {
    const partSalesAdditionalCharges = {
      subTotal: 0,
      tax: 0,
    };

    /**
     * Check the PI flag (goodwrenchFlag) on the embedded service group rate.
     * If PI flag is '0' (No), the WO parts additional charges will be charged out
     * above the rate price. If PI flag on the service group rate is '1'
     * (Yes; meaning that all the pricing is included in the rate), we have to further check the
     * PI flag of the specific WO part additional charges line. If the WO part additional charges line PI flag is '0' (No),
     * charge out the WO part additional charges above the rate price
     */
    const isPriceIncluded = this.isLabourPriceIncluded(labour);

    const stampedCharges = vcrData.partSalesAdditionalCharges.filter(
      (psac: PartSalesAdditionalCharge) => psac.warrantyCaseId === labour.warrantyCase
    );

    stampedCharges.forEach((psac: PartSalesAdditionalCharge) => {
      if (!isPriceIncluded || psac.goodwrenchFlag !== '1') {
        partSalesAdditionalCharges.subTotal += psac.subTotal;
        partSalesAdditionalCharges.tax += psac.pstAmount + psac.gstAmount;
      }
    });

    if (stampedCharges.length === 0 && labour.quoteCaseNumber != null) {
      (vcrData.quoteSalesAdditionalCharges ?? []).forEach((qsac: QuoteSalesAdditionalCharge) => {
        if (qsac.quoteDetailSequence === labour.quoteCaseNumber && (!isPriceIncluded || qsac.goodwrenchFlag !== '1')) {
          partSalesAdditionalCharges.subTotal += qsac.subTotal;
          // A quote row can also predate the DMS tax stamp — a null component derives per getLineTax.
          partSalesAdditionalCharges.tax += this.getLineTax(
            vcrData,
            qsac.gstAmount,
            qsac.pstAmount,
            qsac.subTotal,
            qsac.subTotal
          );
        }
      });
    }

    return partSalesAdditionalCharges;
  }

  getAllAdditionalChargesForWorkOrderLabourLine(
    vcrData: VcrData,
    labour: WorkOrderLabour
  ): { subTotal: number; tax: number } {
    const additionalCharges = this.getAdditionalChargesForWorkOrderLabourLine(vcrData, labour);
    const partSales = this.getPartSalesForWorkOrderLabourLine(vcrData, labour);
    const partSalesAdditionalCharges = this.getPartSalesAdditionalChargesForWorkOrderLabourLine(vcrData, labour);
    return {
      subTotal: additionalCharges.subTotal + partSales.subTotal + partSalesAdditionalCharges.subTotal,
      tax: additionalCharges.tax + partSales.tax + partSalesAdditionalCharges.tax,
    };
  }

  getLineItemRate(quoteLine: QuoteDetail): number {
    if (!quoteLine.embedded?.serviceGroup) {
      return quoteLine.addedByPowerlane === true || quoteLine.rateType === '1'
        ? quoteLine.labourSubTotal
        : quoteLine.rate;
    } else {
      if (
        quoteLine.embedded?.serviceGroup?.embedded?.rate &&
        quoteLine.embedded?.serviceGroup?.embedded?.rate !== null
      ) {
        return quoteLine.rateType !== '1' && quoteLine.embedded?.serviceGroup?.embedded?.rate?.goodwrenchFlag === '1'
          ? quoteLine.embedded?.serviceGroup?.embedded?.rate.rate
          : quoteLine.labourSubTotal;
      } else {
        return quoteLine.labourSubTotal;
      }
    }
  }

  /**
   * The single shared VCR totals calculation used by both the advisor preview
   * (svc-service-lane-ng-ui vcr-preview) and the customer copy (ivcr-ng-ui).
   * Pure: reads vcrData, returns a fresh totals object.
   *
   * Expects `vcrData.subSystemTransactionTypes` in its normalised single-object shape —
   * pass payloads from public/vcr-data through normalizeVcrData first.
   */
  calculateTotals(vcrData: VcrData, acceptedLines: QuoteDetail[] = []): VcrTotals {
    const totals: VcrTotals = {
      subTotal: 0,
      shopSupplies: 0,
      shopSuppliesTaxable: 0,
      tax: 0,
    };

    acceptedLines.forEach((line: QuoteDetail) => {
      if (BOOKABLE_WARRANTY_TYPES.includes(line.warrantySubmissionTypeId)) {
        this.addQuoteLineToTotals(vcrData, line, totals);
      }
    });

    const acceptedSequences = acceptedLines.map((line: QuoteDetail) => line.sequence);
    vcrData.workOrderLabours.forEach((labour: WorkOrderLabour) => {
      if (
        BOOKABLE_WARRANTY_TYPES.includes(labour.warrantySubmissionTypeId) &&
        !acceptedSequences.includes(labour.quoteCaseNumber)
      ) {
        const workOrderLabourAdditionalCharges = this.getAllAdditionalChargesForWorkOrderLabourLine(vcrData, labour);

        totals.subTotal += labour.subTotal + workOrderLabourAdditionalCharges.subTotal;

        const shopSupplies =
          labour.shopSupplyFlag === '1'
            ? (labour.rateTypeId === '1' ? labour.subTotal : labour.rate) *
              (parseFloat(vcrData.sct.shopSuppliesPercent.value) / 100)
            : 0;
        totals.shopSupplies += shopSupplies;
        totals.shopSuppliesTaxable += shopSupplies;

        // An exempt labour (taxable false) derives 0; shop supplies keep their own taxable
        // base above regardless — supplies taxability follows the SSWO flags, not the line's.
        const derivedTaxBase = labour.taxable === false ? 0 : labour.subTotal;
        totals.tax +=
          this.getLineTax(vcrData, labour.gst, labour.pst, derivedTaxBase, derivedTaxBase) +
          workOrderLabourAdditionalCharges.tax;
      }
    });

    if (totals.shopSupplies > parseFloat(vcrData.sct.maxShopSuppliesValue.value)) {
      totals.shopSupplies = parseFloat(vcrData.sct.maxShopSuppliesValue.value);
      totals.shopSuppliesTaxable = parseFloat(vcrData.sct.maxShopSuppliesValue.value);
    } else if (totals.shopSupplies < parseFloat(vcrData.sct.minShopSuppliesValue.value)) {
      totals.shopSupplies = 0;
      totals.shopSuppliesTaxable = 0;
    }

    if (vcrData.associateClass.gstFlag === '1' && vcrData.gstSettings) {
      totals.tax +=
        vcrData.subSystemTransactionTypes?.taxFlag === '1'
          ? totals.shopSuppliesTaxable * (vcrData.gstSettings?.gstPercent / 100)
          : 0;
    }

    if (vcrData.associateClass.pstFlag === '1' && vcrData.pstSettings) {
      totals.tax +=
        vcrData.subSystemTransactionTypes?.pstFlag === '1'
          ? totals.shopSuppliesTaxable * (vcrData.pstSettings?.pstPercent / 100)
          : 0;
    }

    return totals;
  }

  /**
   * A quote line's SHO (shop supplies) charge rows, which the DMS rolls into the line's stamped
   * gst/pst — unlike a work order labour's SHO row, which it taxes on its own.
   */
  private getShopSuppliesChargeForQuoteLine(vcrData: VcrData, line: QuoteDetail): QuoteLineCharges {
    const shopSuppliesCharge: QuoteLineCharges = { subTotal: 0, gstAmount: 0, pstAmount: 0 };

    (vcrData.quoteAdditionalCharges ?? []).forEach((qac: QuoteAdditionalCharge) => {
      if (qac.workOrderChargeTypeId === 'SHO' && qac.quoteDetailSequence === line.sequence) {
        shopSuppliesCharge.subTotal += qac.subTotal;
        shopSuppliesCharge.gstAmount += qac.gstAmount;
        shopSuppliesCharge.pstAmount += qac.pstAmount;
      }
    });

    return shopSuppliesCharge;
  }

  // Prices an accepted recommended quote line into the running totals.
  private addQuoteLineToTotals(vcrData: VcrData, line: QuoteDetail, totals: VcrTotals): void {
    const quoteLineAdditionalCharges = this.getAllAdditionalChargesForQuoteLine(vcrData, line);
    const lineRate = this.getLineItemRate(line);
    const shopSupplies =
      line.shopSupplyFlag === '1' ? line.labourSubTotal * (parseFloat(vcrData.sct.shopSuppliesPercent.value) / 100) : 0;

    totals.subTotal += lineRate + quoteLineAdditionalCharges.subTotal;
    totals.shopSupplies += shopSupplies;
    totals.shopSuppliesTaxable += shopSupplies;

    // Needs the SHO rows present: vcr-preview.component.ts used to strip them at fetch time,
    // which left that screen double-charging supplies tax while the customer copy was correct.
    const shopSuppliesCharge = this.getShopSuppliesChargeForQuoteLine(vcrData, line);

    /**
     * The DMS stamps a quote line's gst/pst against the whole line — its labour plus its parts
     * and additional charges — unlike a work order labour, whose stamped figures cover the
     * labour alone and whose charge rows are therefore booked on top. Adding the charge rows'
     * tax alongside a stamped line figure charged those charges twice (XS-11817): on WO 84875 a
     * labour-free quote line of $28.46 of parts booked $0.24 of PST where the DMS stamped $0.12.
     *
     * That stamp covers the line’s shop supplies too, so booking it whole charged them twice —
     * once inside it, once from shopSuppliesTaxable — leaving the tax over by exactly the line’s
     * supplies tax. Netting the SHO row off per tax type leaves supplies taxed once, at the end,
     * on the figure the VCR computes and caps itself rather than the DMS’s.
     *
     * A line quoted from PowerLane arrives unstamped, and each tax type falls back
     * independently: an unstamped type derives the labour's share and books the charge rows'
     * own figures, which carry their own taxability.
     */
    totals.tax +=
      (isPresent(line.gstAmount)
        ? line.gstAmount - shopSuppliesCharge.gstAmount
        : this.deriveGst(vcrData, lineRate) + quoteLineAdditionalCharges.gstAmount) +
      (isPresent(line.pstAmount)
        ? line.pstAmount - shopSuppliesCharge.pstAmount
        : this.derivePst(vcrData, lineRate) + quoteLineAdditionalCharges.pstAmount);
  }

  /**
   * Sum of the work order's booked services — the "previously accepted" figure. When a labour
   * has a matching accepted quote line, the quote line's pricing wins; otherwise the labour is
   * booked directly (bookable warranty types only).
   */
  getAcceptedServicesSubTotal(vcrData: VcrData, acceptedLines: QuoteDetail[] = []): number {
    let total = 0;
    vcrData.workOrderLabours.forEach((workOrderLabour: WorkOrderLabour) => {
      const matchingQuoteLine = acceptedLines.find(
        (quoteLine: QuoteDetail) => quoteLine.labourOpCode === workOrderLabour.labourOp
      );
      if (matchingQuoteLine) {
        total +=
          this.getLineItemRate(matchingQuoteLine) +
          this.getAllAdditionalChargesForQuoteLine(vcrData, matchingQuoteLine).subTotal;
      } else if (BOOKABLE_WARRANTY_TYPES.includes(workOrderLabour.warrantySubmissionTypeId)) {
        total +=
          workOrderLabour.subTotal +
          this.getAllAdditionalChargesForWorkOrderLabourLine(vcrData, workOrderLabour).subTotal;
      }
    });
    return total;
  }

  /**
   * The displayed total for one work-order case: accepted quote-line pricing first, then the
   * matching labour, else 'Needs Assessment' when the case has no priced labour yet.
   */
  getServiceTotal(vcrData: VcrData, workOrderCase: WorkOrderCase, acceptedLines: QuoteDetail[] = []): number | string {
    const matchingQuoteLine = acceptedLines.find(
      (quoteLine: QuoteDetail) => quoteLine.labourOpCode === workOrderCase.labourOp
    );
    if (matchingQuoteLine) {
      return (
        this.getLineItemRate(matchingQuoteLine) +
        this.getAllAdditionalChargesForQuoteLine(vcrData, matchingQuoteLine).subTotal
      );
    }

    const matchingLabour = vcrData.workOrderLabours.find(
      (workOrderLabour: WorkOrderLabour) => workOrderLabour.sequence === workOrderCase.sequence
    );
    if (matchingLabour) {
      return (
        matchingLabour.subTotal + this.getAllAdditionalChargesForWorkOrderLabourLine(vcrData, matchingLabour).subTotal
      );
    }

    return 'Needs Assessment';
  }

  /**
   * Reprices the customer copy's raw public/vcr-data labours before the totals are calculated:
   * Toyota-MOC labours take the matching OEM service-group rate; price-included (goodwrenchFlag)
   * non-flat-rate labours take the service-group rate, with gst/pst recomputed on the new base.
   * Mutates the labours in place — the customer copy's templates render the repriced values.
   * The advisor preview applies its equivalent repricing while assembling vcrData at fetch time.
   */
  applyCustomerPayloadRepricing(vcrData: VcrData): void {
    vcrData.workOrderLabours.forEach((woLabour: WorkOrderLabour) => {
      const serviceGroup = woLabour.embedded?.serviceGroup;
      const serviceGroupRate = serviceGroup?.embedded?.rate;

      const oemServiceGroupRates = serviceGroup?.embedded?.oemServiceGroupRates?.length
        ? serviceGroup.embedded.oemServiceGroupRates
        : undefined;

      /**
       * If the service is a Toyota MOC service, there will be oemServiceGroupRates embedded on the
       * serviceGroup; if there are none, it is a standard service group rate
       */
      if (oemServiceGroupRates) {
        const oemServiceGroupRateForOpCode = oemServiceGroupRates.find(
          (oemServiceGroupRate) => oemServiceGroupRate.opCode === woLabour.labourOp
        );
        if (serviceGroup && oemServiceGroupRateForOpCode && oemServiceGroupRateForOpCode.goodwrenchFlag) {
          woLabour.subTotal = oemServiceGroupRateForOpCode.total;

          woLabour.gst =
            vcrData.associateClass.gstFlag === '1' && vcrData.gstSettings
              ? woLabour.gst !== 0
                ? oemServiceGroupRateForOpCode.total * (vcrData.gstSettings.gstPercent / 100)
                : 0
              : 0;

          woLabour.pst =
            vcrData.associateClass.pstFlag === '1' && vcrData.pstSettings
              ? woLabour.pst !== 0
                ? oemServiceGroupRateForOpCode.total * (vcrData.pstSettings.pstPercent / 100)
                : 0
              : 0;
        }
      } else {
        if (serviceGroupRate && serviceGroupRate.goodwrenchFlag === '1' && serviceGroupRate.rateType !== '1') {
          woLabour.subTotal = serviceGroupRate.rate;
          woLabour.gst =
            vcrData.associateClass.gstFlag === '1' && vcrData.gstSettings
              ? woLabour.gst !== 0
                ? serviceGroupRate.rate * (vcrData.gstSettings.gstPercent / 100)
                : 0
              : 0;
          woLabour.pst =
            vcrData.associateClass.pstFlag === '1' && vcrData.pstSettings
              ? woLabour.pst !== 0
                ? serviceGroupRate.rate * (vcrData.pstSettings.pstPercent / 100)
                : 0
              : 0;
        }
      }
    });
  }

  /**
   * The public/vcr-data endpoint sends subSystemTransactionTypes as the full recordset, while the
   * advisor preview slices [0] at fetch time and the VcrData model declares the single-object
   * shape. Normalise the recordset shape at the boundary so the totals maths reads one shape.
   */
  normalizeVcrData(payload: VcrData): VcrData {
    const subSystemTransactionTypes: SubSystemTransactionType = Array.isArray(payload.subSystemTransactionTypes)
      ? payload.subSystemTransactionTypes[0] ?? null
      : payload.subSystemTransactionTypes;
    return new VcrData({ ...payload, subSystemTransactionTypes });
  }

  determineVcrSendMethod(
    contactFormValues: { messagePreference: number; messagePreferenceContactInfo: string },
    messageTypes: MessageType[],
    currentAssociateMessagePreference: string
  ) {
    if (contactFormValues.messagePreferenceContactInfo !== null) {
      return messageTypes.find((messageType) => messageType.id === contactFormValues.messagePreference).description;
    } else {
      return currentAssociateMessagePreference ? currentAssociateMessagePreference : 'None';
    }
  }
}
